/**
 * LeadDetail — the assembled Scientific Lead Intelligence Profile for one lead:
 * identity + match confidence, the five explained scores + composite, research
 * topics, publications, grants, co-authors, lab/PI, conference abstracts, the
 * AI recommendation summary, and source-attribution badges throughout. Includes
 * re-enrich, CSV export, the feedback/correction loop, and a link to the
 * editable outreach draft.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { LeadIntelligenceProfile, SourceSystem } from '@aurora/shared';
import { api, ApiClientError } from '../api/client';
import { ScoreCard } from '../components/ScoreCard';
import { SourceBadge, SourceBadgeRow } from '../components/SourceBadge';
import { FeedbackControls } from '../components/FeedbackControls';

/** Collect the distinct sources that attribute a given entity id. */
function sourcesForEntity(
  profile: LeadIntelligenceProfile,
  entityId: string,
): SourceSystem[] {
  return profile.attributions
    .filter((a) => a.entityId === entityId)
    .map((a) => a.source);
}

export function LeadDetail(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [profile, setProfile] = useState<LeadIntelligenceProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      setProfile(await api.getProfile(id));
    } catch (e) {
      setError(
        e instanceof ApiClientError ? e.message : 'Failed to load profile',
      );
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const onReenrich = async (): Promise<void> => {
    if (!id) return;
    setBusy('reenrich');
    try {
      await api.reenrich(id);
      await load();
    } catch (e) {
      setError(e instanceof ApiClientError ? e.message : 'Re-enrich failed');
    } finally {
      setBusy(null);
    }
  };

  const onExport = async (): Promise<void> => {
    if (!id) return;
    setBusy('export');
    try {
      const res = await api.downloadLeadCsv(id);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `aurora-lead-${id}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setBusy(null);
    }
  };

  if (loading) return <div className="centered muted">Loading profile…</div>;
  if (error) return <div className="form-error">{error}</div>;
  if (!profile) return <div className="muted">Profile not found.</div>;

  const {
    lead,
    enrichment,
    scientist,
    institution,
    lab,
    scores,
    recommendation,
  } = profile;
  const confidencePct = Math.round(enrichment.matchConfidence * 100);

  return (
    <div className="lead-detail">
      <div className="page-head">
        <div>
          <Link to="/" className="back-link">
            ← Leads
          </Link>
          <h1>{lead.fullName}</h1>
          <p className="muted">
            {[lead.title, lead.institutionRaw].filter(Boolean).join(' · ') ||
              'No title / institution provided'}
          </p>
        </div>
        <div className="head-actions">
          <button
            type="button"
            className="btn"
            onClick={onReenrich}
            disabled={busy !== null}
          >
            {busy === 'reenrich' ? 'Re-enriching…' : 'Re-enrich'}
          </button>
          <button
            type="button"
            className="btn"
            onClick={onExport}
            disabled={busy !== null}
          >
            {busy === 'export' ? 'Exporting…' : 'Export CSV'}
          </button>
        </div>
      </div>

      {/* Identity + confidence */}
      <section className="card">
        <div className="identity-head">
          <h2>Resolved identity</h2>
          <span className={`pill pill-${enrichment.status}`}>
            {enrichment.status.replace('_', ' ')}
          </span>
        </div>
        {scientist ? (
          <div className="identity-grid">
            <div>
              <span className="muted">Scientist</span>
              <strong>{scientist.fullName}</strong>
            </div>
            <div>
              <span className="muted">Institution</span>
              <strong>{institution?.name ?? '—'}</strong>
            </div>
            <div>
              <span className="muted">Lab / PI</span>
              <strong>{lab?.name ?? '—'}</strong>
            </div>
            <div>
              <span className="muted">Match confidence</span>
              <strong>{confidencePct}%</strong>
            </div>
          </div>
        ) : (
          <p className="muted">
            No scientist identity has been resolved with sufficient confidence.
            {enrichment.status === 'needs_review' &&
              ' This lead is parked for human review.'}
          </p>
        )}
        <FeedbackControls leadId={lead.id} onSubmitted={load} />
      </section>

      {/* Scores */}
      <section className="card">
        <div className="section-head">
          <h2>Scores</h2>
          {scores && (
            <span className="composite">
              Composite <strong>{scores.composite}</strong>
            </span>
          )}
        </div>
        {scores ? (
          <div className="score-grid">
            {Object.values(scores.scores).map((s) => (
              <ScoreCard key={s.category} score={s} />
            ))}
          </div>
        ) : (
          <p className="muted">
            Not yet scored — scores are computed once enrichment completes.
          </p>
        )}
      </section>

      {/* Recommendation */}
      <section className="card">
        <div className="section-head">
          <h2>AI recommendation</h2>
          <Link to={`/leads/${lead.id}/outreach`} className="btn btn-small">
            Open outreach editor
          </Link>
        </div>
        {recommendation ? (
          <div className="rec-summary">
            <p>
              <strong>Summary.</strong> {recommendation.engagementSummary}
            </p>
            <p>
              <strong>Follow-up angle.</strong> {recommendation.followUpAngle}
            </p>
            <p className="rationale muted">
              <strong>Why:</strong> {recommendation.rationale}
            </p>
            <p className="advisory-note muted">
              Advisory only — the draft is editable and is never sent
              automatically.
            </p>
          </div>
        ) : (
          <p className="muted">
            No recommendation yet. Open the outreach editor to generate one.
          </p>
        )}
      </section>

      {/* Research topics */}
      {profile.topics.length > 0 && (
        <section className="card">
          <h2>Research topics</h2>
          <div className="topic-chips">
            {profile.topics.map(({ topic, classification }) => (
              <div
                key={topic.id}
                className="topic-chip"
                title={classification.evidence ?? ''}
              >
                <span className="topic-name">{topic.name}</span>
                <span className="topic-conf">
                  {Math.round(classification.confidence * 100)}%
                </span>
              </div>
            ))}
          </div>
          <SourceBadge source="derived" />
        </section>
      )}

      {/* Publications */}
      <section className="card">
        <h2>Publications ({profile.publications.length})</h2>
        {profile.publications.length === 0 ? (
          <p className="muted">No publications found in public sources.</p>
        ) : (
          <ul className="fact-list">
            {profile.publications.slice(0, 25).map((p) => (
              <li key={p.id}>
                <div className="fact-main">
                  {p.title}
                  {p.year != null && <span className="muted"> ({p.year})</span>}
                </div>
                <div className="fact-meta">
                  {p.journal && <span className="muted">{p.journal}</span>}
                  <SourceBadgeRow sources={sourcesForEntity(profile, p.id)} />
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Grants */}
      <section className="card">
        <h2>Grants ({profile.grants.length})</h2>
        {profile.grants.length === 0 ? (
          <p className="muted">No grants found in NIH RePORTER.</p>
        ) : (
          <ul className="fact-list">
            {profile.grants.slice(0, 25).map((g) => (
              <li key={g.id}>
                <div className="fact-main">{g.title ?? 'Untitled grant'}</div>
                <div className="fact-meta">
                  <span className="muted">
                    {[g.agency, g.fiscalYear ? `FY${g.fiscalYear}` : null]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                  <SourceBadgeRow sources={sourcesForEntity(profile, g.id)} />
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Co-authors */}
      {profile.coAuthors.length > 0 && (
        <section className="card">
          <h2>Frequent co-authors</h2>
          <ul className="coauthor-list">
            {profile.coAuthors.slice(0, 15).map((c) => (
              <li key={c.scientistId}>
                <span>{c.fullName}</span>
                <span className="muted">
                  {c.institutionName ?? '—'} · {c.sharedPublications} shared
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Conference abstracts */}
      {profile.conferenceAbstracts.length > 0 && (
        <section className="card">
          <h2>Conference abstracts</h2>
          <ul className="fact-list">
            {profile.conferenceAbstracts.map((a) => (
              <li key={a.id}>
                <div className="fact-main">{a.title}</div>
                <div className="fact-meta">
                  <span className="muted">
                    {a.conference}
                    {a.year ? ` ${a.year}` : ''}
                  </span>
                  <SourceBadgeRow sources={sourcesForEntity(profile, a.id)} />
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
